import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateDepartment, Department, UpdateDepartment } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['departments', companyId] as const;

export function useDepartments(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () =>
      api<Department[]>(`/companies/${companyId}/departments?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useDepartment(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['departments', companyId, id],
    queryFn: () => api<Department>(`/companies/${companyId}/departments/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateDepartment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateDepartment, 'companyId'>) =>
      apiJson<typeof input, Department>('POST', `/companies/${companyId}/departments`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateDepartment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDepartment }) =>
      apiJson<UpdateDepartment, Department>(
        'PATCH',
        `/companies/${companyId}/departments/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteDepartment(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, Department>('DELETE', `/companies/${companyId}/departments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
